/**
 * Room3DLoadingOverlay - Cinematic 3D Room Loading Screen
 *
 * Displays a luxury atmospheric loader with a slowly rotating random card from the deck,
 * double-sided 3D card physics (front face with court/pip art, back face with active card-back theme,
 * realistic cardstock edge bevels), dynamic lighting, and room setup progress indicator.
 */

let activeRoom3DLoaderInstance = null;

export class Room3DLoadingOverlay {
  /**
   * Displays the 3D Room Loader with a slowly rotating random card.
   *
   * @param {HTMLElement} [container] - Container element (default: #board3DContainer or #boardTracks)
   * @param {Object} [options]
   * @param {Object} [options.settings] - Active game settings
   * @param {Object} [options.card] - Explicit card to display (optional)
   * @param {Array<Object>} [options.deck] - Source deck to choose random card from (optional)
   * @param {string} [options.theme] - Active room theme id
   * @param {string} [options.cardBack] - Active card back style id
   * @param {number} [options.minDuration] - Minimum display milliseconds before hiding (default 1350ms)
   * @param {Function} [options.onComplete] - Callback fired when loader finishes and dismisses
   * @returns {Room3DLoadingOverlay}
   */
  static show(container = null, options = {}) {
    if (typeof document === 'undefined') {
      if (typeof options.onComplete === 'function') options.onComplete();
      return null;
    }

    if (activeRoom3DLoaderInstance) {
      activeRoom3DLoaderInstance.close(false);
    }

    const instance = new Room3DLoadingOverlay(container, options);
    activeRoom3DLoaderInstance = instance;
    instance.mount();
    return instance;
  }

  /**
   * Returns the current active instance if any.
   * @returns {Room3DLoadingOverlay|null}
   */
  static getActive() {
    return activeRoom3DLoaderInstance;
  }

  /**
   * Notifies the active loader that the 3D renderer is ready.
   * @param {Object} [renderer3D]
   */
  static markRendererReady(renderer3D = null) {
    if (activeRoom3DLoaderInstance) {
      activeRoom3DLoaderInstance.markRendererReady(renderer3D);
    }
  }

  /**
   * Updates progress on the active loader.
   * @param {string} text
   * @param {number} percent
   */
  static updateProgress(text, percent) {
    if (activeRoom3DLoaderInstance) {
      activeRoom3DLoaderInstance.updateProgress(text, percent);
    }
  }

  /**
   * Immediately closes the active loader without animations (e.g. on error).
   */
  static hideImmediate() {
    if (activeRoom3DLoaderInstance) {
      activeRoom3DLoaderInstance.close(false);
    }
  }

  /**
   * Smoothly finishes and dismisses the active loader.
   * @param {Function} [callback]
   */
  static finish(callback = null) {
    if (activeRoom3DLoaderInstance) {
      activeRoom3DLoaderInstance.finish(callback);
    } else if (typeof callback === 'function') {
      callback();
    }
  }

  constructor(container = null, options = {}) {
    this.container = container;
    this.options = options;
    this.settings = options.settings || (typeof window !== 'undefined' ? window.settings : {}) || {};
    this.minDuration = options.minDuration != null ? options.minDuration : 1600;
    this.onComplete = options.onComplete || (() => {});

    this.card = options.card || Room3DLoadingOverlay.pickRandomCard(options.deck || (typeof window !== 'undefined' ? window.FULL_DECK : null));
    this.cardBack = options.cardBack || this.settings.cardBack || 'classic-blue';
    this.themeId = options.theme || this.settings.roomTheme || this.settings.tableEnvironment || 'pub';

    this.startTime = Date.now();
    this.overlayEl = null;
    this.progressFillEl = null;
    this.statusTextEl = null;
    this.currentProgress = 0.15;
    this.isClosing = false;
    this.rendererIsReady = false;
    this._safetyTimeout = null;
    this._tipInterval = null;
  }

  /**
   * Resolves a random card from the provided deck or standard 52-card distribution.
   * @param {Array<Object>} [deck]
   * @returns {Object} Normalized card data
   */
  static pickRandomCard(deck = null) {
    if (Array.isArray(deck) && deck.length > 0) {
      const idx = Math.floor(Math.random() * deck.length);
      return Room3DLoadingOverlay.normalizeCard(deck[idx]);
    }

    const ranks = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
    const suits = ['S', 'H', 'D', 'C'];
    const r = ranks[Math.floor(Math.random() * ranks.length)];
    const s = suits[Math.floor(Math.random() * suits.length)];
    return Room3DLoadingOverlay.normalizeCard({ r, s, id: `${r}${s}` });
  }

  /**
   * Normalizes rank, suit, symbol, color and friendly labels for a card.
   * @param {Object} rawCard
   * @returns {Object}
   */
  static normalizeCard(rawCard) {
    if (!rawCard) {
      return {
        r: 'A',
        s: 'S',
        rank: 'A',
        suit: 'S',
        rankName: 'Ace',
        suitName: 'Spades',
        suitSymbol: '♠',
        isRed: false,
        isWild: false,
        fullName: 'Ace of Spades ♠',
        id: 'AS'
      };
    }

    const isWild = Boolean(rawCard.wild || rawCard.isWild || rawCard.r === 0 || rawCard.rank === 0);
    if (isWild && !rawCard.playAs) {
      return {
        r: 'JOKER',
        s: 'J',
        rank: '★',
        suit: 'J',
        rankName: 'Joker',
        suitName: 'Wild',
        suitSymbol: '★',
        isRed: false,
        isWild: true,
        fullName: 'Wild Joker ★',
        id: rawCard.id || 'JOKER'
      };
    }

    let rank = String((rawCard.playAs ? rawCard.playAs.r : rawCard.r) || rawCard.rank || 'A').toUpperCase();
    if (rank === '1') rank = 'A';
    if (rank === '11') rank = 'J';
    if (rank === '12') rank = 'Q';
    if (rank === '13') rank = 'K';

    let suit = String((rawCard.playAs ? rawCard.playAs.s : rawCard.s) || rawCard.suit || 'S').toUpperCase().charAt(0);
    if (!['S', 'H', 'D', 'C'].includes(suit)) {
      if (suit === '♠') suit = 'S';
      else if (suit === '♥') suit = 'H';
      else if (suit === '♦') suit = 'D';
      else if (suit === '♣') suit = 'C';
      else suit = 'S';
    }

    const suitSymbols = { S: '♠', H: '♥', D: '♦', C: '♣' };
    const suitNames = { S: 'Spades', H: 'Hearts', D: 'Diamonds', C: 'Clubs' };
    const rankNames = {
      A: 'Ace',
      '2': '2',
      '3': '3',
      '4': '4',
      '5': '5',
      '6': '6',
      '7': '7',
      '8': '8',
      '9': '9',
      '10': '10',
      J: 'Jack',
      Q: 'Queen',
      K: 'King'
    };

    const isRed = (suit === 'H' || suit === 'D');
    const rankName = rankNames[rank] || rank;
    const suitName = suitNames[suit] || suit;
    const suitSymbol = suitSymbols[suit] || suit;

    return {
      r: rank,
      s: suit,
      rank,
      suit,
      rankName,
      suitName,
      suitSymbol,
      isRed,
      isWild: false,
      fullName: `${rankName} of ${suitName} ${suitSymbol}`,
      id: rawCard.id || `${rank}${suit}`
    };
  }

  /**
   * Resolves user-friendly themed titles for the room environment header.
   * @param {string} themeId
   * @returns {string}
   */
  static getThemeTitle(themeId) {
    const titles = {
      pub: 'The English Pub & Fireplace Hearth',
      vegas: 'The High-Roller Penthouse Suite',
      cabin: 'The Alpine Mountain Lodge',
      velvet: 'The Velvet Underground Speakeasy',
      industrial: 'The Industrial Loft & Warehouse',
      nordic: 'The Minimalist Nordic Studio',
      none: 'Custom Themed Cribbage Table'
    };
    return titles[themeId] || titles.pub;
  }

  /**
   * Mounts the loader overlay DOM element into the target container.
   */
  mount() {
    if (typeof document === 'undefined') return;

    let target = this.container;
    if (!target) {
      target = document.getElementById('board3DContainer') ||
               document.getElementById('boardTracks') ||
               document.getElementById('cribBoard') ||
               document.getElementById('game');
    }
    if (!target) return;

    // Remove any orphaned loader overlay elements
    const oldOverlays = target.querySelectorAll('.room3d-loading-overlay');
    oldOverlays.forEach(el => el.remove());

    const isGlobal = Boolean(this.options.isGlobal || target === document.body || target?.id === 'game');
    const overlay = document.createElement('div');
    overlay.className = `room3d-loading-overlay theme-${this.themeId}${isGlobal ? ' is-global-overlay' : ''}`;
    overlay.id = 'room3dLoadingOverlay';
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');
    overlay.setAttribute('aria-label', 'Loading 3D Room and Tabletop');

    const is3DOnly = Boolean(this.options.is3DOnly);
    const themeTitle = is3DOnly ? 'Interactive 3D Cribbage Board' : Room3DLoadingOverlay.getThemeTitle(this.themeId);
    const badgeText = is3DOnly ? '3D CRIBBAGE BOARD' : '3D ROOM ENVIRONMENT';
    const mainTitle = is3DOnly ? 'Loading 3D Cribbage Board' : 'Entering 3D Cribbage Room';
    const card = this.card;

    overlay.innerHTML = `
      <div class="room3d-loader-backdrop-glow"></div>

      <!-- Header Section -->
      <div class="room3d-loader-header">
        <div class="room3d-loader-badge">
          <span class="room3d-loader-badge-dot"></span>
          <span>${badgeText}</span>
        </div>
        <h2 class="room3d-loader-title">${mainTitle}</h2>
        <div class="room3d-loader-subtitle" id="room3dLoaderTheme">${themeTitle}</div>
      </div>

      <!-- 3D Rotating Card Section -->
      <div class="room3d-card-viewport">
        <div class="room3d-card-scene">
          <div class="room3d-card-3d" id="room3dSpinningCard">
            <!-- Front Face -->
            <div class="room3d-card-face room3d-card-front ${card.isRed ? 'is-red' : 'is-black'}">
              <div class="room3d-card-corner top-left">
                <span class="room3d-card-rank">${card.rank}</span>
                <span class="room3d-card-suit">${card.suitSymbol}</span>
              </div>
              <div class="room3d-card-center">
                ${this.renderCardCenterMarkup(card)}
              </div>
              <div class="room3d-card-corner bottom-right">
                <span class="room3d-card-rank">${card.rank}</span>
                <span class="room3d-card-suit">${card.suitSymbol}</span>
              </div>
              <div class="room3d-card-gloss"></div>
            </div>

            <!-- Back Face -->
            <div class="room3d-card-face room3d-card-back back-${this.cardBack}">
              <div class="room3d-card-back-pattern"></div>
              <div class="room3d-card-back-frame">
                <div class="room3d-card-back-crest">
                  <span class="room3d-crest-sym">✦</span>
                  <span class="room3d-crest-label">CRAZY CRIBBAGE</span>
                </div>
              </div>
              <div class="room3d-card-gloss"></div>
            </div>

            <!-- 3D Card Edge Thickness Layers -->
            <div class="room3d-card-edge edge-left"></div>
            <div class="room3d-card-edge edge-right"></div>
            <div class="room3d-card-edge edge-top"></div>
            <div class="room3d-card-edge edge-bottom"></div>
          </div>
          <!-- Ground Breathing Shadow -->
          <div class="room3d-card-shadow"></div>
        </div>
      </div>

      <!-- Card Identity Pill -->
      <div class="room3d-card-draw-pill">
        <span class="room3d-card-draw-icon">🎴</span>
        <span class="room3d-card-draw-label">Random Draw:</span>
        <strong class="room3d-card-draw-name ${card.isRed ? 'is-red' : ''}">${card.fullName}</strong>
      </div>

      <!-- Footer / Loading Progress Section -->
      <div class="room3d-loader-footer">
        <div class="room3d-loader-status-wrap">
          <span class="room3d-loader-spinner-icon" aria-hidden="true">⏳</span>
          <span class="room3d-loader-status" id="room3dLoaderStatus">Initializing 3D graphics &amp; lighting…</span>
        </div>
        <div class="room3d-loader-progress-bar">
          <div class="room3d-loader-progress-fill" id="room3dLoaderProgressFill" style="width: 15%;"></div>
          <div class="room3d-loader-progress-glow"></div>
        </div>
        <div class="room3d-loader-tip" id="room3dLoaderTip">
          <span class="room3d-tip-icon">💡</span>
          <span class="room3d-tip-text" id="room3dLoaderTipText">Drag anywhere on the 3D tabletop to orbit the camera view.</span>
        </div>
      </div>
    `;

    target.appendChild(overlay);
    this.overlayEl = overlay;
    this.progressFillEl = overlay.querySelector('#room3dLoaderProgressFill');
    this.statusTextEl = overlay.querySelector('#room3dLoaderStatus');

    this.startAmbientTipRotation();

    // Safety timeout: ensure loader never traps the user indefinitely (max 12s)
    this._safetyTimeout = setTimeout(() => {
      if (!this.isClosing) {
        this.finish();
      }
    }, 12000);
  }

  /**
   * Generates SVG/HTML art for court cards, aces, jokers, and pip distributions.
   * @param {Object} card
   * @returns {string}
   */
  renderCardCenterMarkup(card) {
    if (card.isWild) {
      return `
        <div class="room3d-art-court joker">
          <div class="court-icon">🎭</div>
          <div class="court-title">JOKER</div>
          <div class="court-stars">★ ★ ★</div>
        </div>
      `;
    }

    if (card.rank === 'K') {
      return `
        <div class="room3d-art-court king">
          <div class="court-crown">👑</div>
          <div class="court-figure">♚</div>
          <div class="court-title">KING</div>
          <div class="court-suit-sym">${card.suitSymbol}</div>
        </div>
      `;
    }

    if (card.rank === 'Q') {
      return `
        <div class="room3d-art-court queen">
          <div class="court-crown">♕</div>
          <div class="court-figure">♛</div>
          <div class="court-title">QUEEN</div>
          <div class="court-suit-sym">${card.suitSymbol}</div>
        </div>
      `;
    }

    if (card.rank === 'J') {
      return `
        <div class="room3d-art-court jack">
          <div class="court-crown">🛡️</div>
          <div class="court-figure">♞</div>
          <div class="court-title">JACK</div>
          <div class="court-suit-sym">${card.suitSymbol}</div>
        </div>
      `;
    }

    if (card.rank === 'A') {
      return `
        <div class="room3d-art-ace">
          <div class="ace-large-suit">${card.suitSymbol}</div>
          <div class="ace-flourish">✦ ACE ✦</div>
        </div>
      `;
    }

    // Number cards (2 to 10)
    const count = parseInt(card.rank, 10) || 5;
    let pipsHtml = '';
    for (let i = 0; i < count; i++) {
      pipsHtml += `<span class="room3d-pip-item">${card.suitSymbol}</span>`;
    }
    return `
      <div class="room3d-art-pips pips-${count}">
        ${pipsHtml}
      </div>
    `;
  }

  /**
   * Cycles ambient cribbage tips smoothly while the room loads.
   */
  startAmbientTipRotation() {
    const tips = [
      'Drag anywhere on the 3D tabletop to orbit the camera view.',
      'Use the mouse scroll wheel or pinch to zoom in on your cards or pegs.',
      'Double-click the 3D board resize handle to reset the board height.',
      'Lowest cut card earns the first deal — Ace is lowest in Cribbage!',
      'Watch your opponents in radial seats for real-time play gestures.'
    ];
    let tipIdx = 0;
    this._tipInterval = setInterval(() => {
      if (this.isClosing || !this.overlayEl) return;
      const el = this.overlayEl.querySelector('#room3dLoaderTipText');
      if (el) {
        tipIdx = (tipIdx + 1) % tips.length;
        el.style.opacity = '0';
        setTimeout(() => {
          if (el) {
            el.textContent = tips[tipIdx];
            el.style.opacity = '1';
          }
        }, 200);
      }
    }, 2800);
  }

  /**
   * Updates loading progress percentage and text.
   * @param {string} text
   * @param {number} percent - 0.0 to 1.0
   */
  updateProgress(text, percent) {
    if (this.isClosing) return;
    this.currentProgress = Math.max(this.currentProgress, Math.min(1.0, percent));
    if (this.statusTextEl && text) {
      this.statusTextEl.textContent = text;
    }
    if (this.progressFillEl) {
      this.progressFillEl.style.width = `${Math.round(this.currentProgress * 100)}%`;
    }
  }

  /**
   * Marks the 3D renderer as ready. Waits for Babylon ready & minDuration before dismissing.
   * @param {Object} [renderer3D]
   */
  markRendererReady(renderer3D = null) {
    this.rendererIsReady = true;
    this.updateProgress('Finalizing 3D tabletop environment…', 0.95);

    const onReadyCheck = () => {
      const elapsed = Date.now() - this.startTime;
      const remainingTime = Math.max(0, this.minDuration - elapsed);

      setTimeout(() => {
        this.finish();
      }, remainingTime);
    };

    if (renderer3D && renderer3D.scene && typeof renderer3D.scene.executeWhenReady === 'function') {
      renderer3D.scene.executeWhenReady(() => {
        // Render 1 frame before dismissing
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(onReadyCheck);
        } else {
          onReadyCheck();
        }
      });
    } else {
      onReadyCheck();
    }
  }

  /**
   * Completes the loading sequence with an elegant exit animation.
   * @param {Function} [callback]
   */
  finish(callback = null) {
    if (this.isClosing) return;
    this.isClosing = true;

    if (this._safetyTimeout) {
      clearTimeout(this._safetyTimeout);
      this._safetyTimeout = null;
    }
    if (this._tipInterval) {
      clearInterval(this._tipInterval);
      this._tipInterval = null;
    }

    this.updateProgress('Room Ready — Let’s Play!', 1.0);

    setTimeout(() => {
      if (this.overlayEl) {
        this.overlayEl.classList.add('room3d-loader-hiding');
      }

      setTimeout(() => {
        this.close(true);
        if (typeof callback === 'function') callback();
      }, 420);
    }, 240);
  }

  /**
   * Closes and cleans up the overlay from DOM.
   * @param {boolean} triggerComplete - whether to fire onComplete callback
   */
  close(triggerComplete = true) {
    if (this._safetyTimeout) {
      clearTimeout(this._safetyTimeout);
      this._safetyTimeout = null;
    }
    if (this._tipInterval) {
      clearInterval(this._tipInterval);
      this._tipInterval = null;
    }

    if (this.overlayEl) {
      try {
        if (typeof this.overlayEl.remove === 'function') {
          this.overlayEl.remove();
        } else if (this.overlayEl.parentNode) {
          this.overlayEl.parentNode.removeChild(this.overlayEl);
        }
      } catch (_) {}
      this.overlayEl = null;
    }

    if (activeRoom3DLoaderInstance === this) {
      activeRoom3DLoaderInstance = null;
    }

    if (triggerComplete && typeof this.onComplete === 'function') {
      try { this.onComplete(); } catch (_) {}
    }
  }
}
